import React, { useState, useContext } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {
  Alert,
  useTheme,
  Autocomplete,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  IconButton,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import FlexBetween from "components/FlexBetween";
import AuthLayout, { authInputSx, authFieldLabelSx, authButtonSx } from "components/AuthLayout";
import OTPInput from "components/OTPInput";
import { ConstantsContext } from "utils/const";
import { generateUniqueId } from "utils";
import { validateWhatsappNumber } from "utils/phoneValidation";
import { useTranslation } from "react-i18next";

const baseUrl = process.env.REACT_APP_BASE_URL + "/public";

export default function CustomerSignup({ updateCustomerState }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const constants = useContext(ConstantsContext);
  const countryRules = constants.countryRules;

  // State management
  // Steps: "whatsapp-check" -> "verify-otp" -> "signup-form"
  const [step, setStep] = useState("whatsapp-check");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [checkingWhatsapp, setCheckingWhatsapp] = useState(false);
  const [whatsappExists, setWhatsappExists] = useState(false);
  const [signupFailed, setSignupFailed] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // OTP verification state
  const otpLength = 6;
  const [otp, setOtp] = useState(new Array(otpLength).fill(""));
  const [otpRef, setOtpRef] = useState("");
  const [otpJobId, setOtpJobId] = useState("");
  const [otpDeliveryStatus, setOtpDeliveryStatus] = useState("");
  const [otpVerifyFailed, setOtpVerifyFailed] = useState(false);
  const [otpErrorMsg, setOtpErrorMsg] = useState("");
  const [resendSeconds, setResendSeconds] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);

  // New form fields state
  const [defaultRoute, setDefaultRoute] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [pickupPoints, setPickupPoints] = useState([]);
  const [loadingPickupPoints, setLoadingPickupPoints] = useState(false);

  // Location and delivery preferences
  const [isVientianeResident, setIsVientianeResident] = useState(null); // null = not selected, true/false = selected
  const [preferDoorToDoor, setPreferDoorToDoor] = useState(false);
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedLogisticCompany, setSelectedLogisticCompany] = useState("");
  const [branchName, setBranchName] = useState("");
  const [addressDetail, setAddressDetail] = useState("");
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  // Country code setup similar to WhatsApp login
  const uniqueCountryRules = countryRules.filter(
    (country, index, self) =>
      index === self.findIndex((c) => c.phone_code === country.phone_code)
  );

  const autocompleteOptions = uniqueCountryRules.map((country) => ({
    label: `(${country.phone_code}) ${country.code} - ${country.name}`,
    value: country.phone_code,
  }));

  // Set default country to Laos
  React.useEffect(() => {
    const laosRule = countryRules.find((country) => country.code === "LA");
    if (laosRule) {
      setCountryCode(laosRule.phone_code);
    }
  }, [countryRules]);

  // Resend OTP timer
  React.useEffect(() => {
    let intervalId;
    if (step === "verify-otp" && resendSeconds > 0) {
      intervalId = setInterval(() => {
        setResendSeconds((s) => s - 1);
      }, 1000);
    } else if (resendSeconds === 0) {
      setResendDisabled(false);
      clearInterval(intervalId);
    }
    return () => clearInterval(intervalId);
  }, [step, resendSeconds]);

  const requestSignupOtp = async (phone) => {
    const ref = generateUniqueId(10);
    setOtpRef(ref);
    const response = await axios.post(
      baseUrl + "/otp-request",
      { phone, ref, otpType: "signup" },
      { withCredentials: true }
    );
    setOtpJobId(response?.data?.jobId || "");
    setOtpDeliveryStatus("queued");
    return ref;
  };

  React.useEffect(() => {
    if (step !== "verify-otp" || !otpJobId || !whatsappNumber || !otpRef) {
      return;
    }

    let intervalId;
    let stopped = false;

    const pollOtpDeliveryStatus = async () => {
      try {
        const response = await axios.post(
          baseUrl + "/otp-delivery-status",
          {
            jobId: otpJobId,
            phone: whatsappNumber,
            ref: otpRef,
            otpType: "signup",
          },
          { withCredentials: true }
        );

        const status = response?.data?.deliveryStatus;
        setOtpDeliveryStatus(status || "");

        if (status === "failed" || status === "cancelled") {
          setOtpVerifyFailed(true);
          setOtpErrorMsg(
            response?.data?.lastErrorMessage ||
              "OTP delivery failed. Please tap Resend OTP."
          );
          setResendDisabled(false);
          setResendSeconds(0);
          clearInterval(intervalId);
        }

        if (status === "sent") {
          clearInterval(intervalId);
        }
      } catch (error) {
        if (!stopped) {
          console.log("OTP delivery status check failed:", error?.message);
        }
      }
    };

    pollOtpDeliveryStatus();
    intervalId = setInterval(pollOtpDeliveryStatus, 3000);

    return () => {
      stopped = true;
      clearInterval(intervalId);
    };
  }, [step, otpJobId, whatsappNumber, otpRef]);

  const startResendTimer = () => {
    setResendSeconds(60);
    setResendDisabled(true);
  };

  const handleOtpComplete = async (otpValue) => {
    setIsLoading(true);
    setOtpVerifyFailed(false);
    setOtpErrorMsg("");
    try {
      const res = await axios.post(
        baseUrl + "/otp-verify",
        {
          phone: whatsappNumber,
          otp: otpValue,
          ref: otpRef,
          otpType: "signup",
        },
        { withCredentials: true }
      );
      if (res.status === 200) {
        // OTP verified — backend has set the signup-verified cookie.
        setStep("signup-form");
      }
    } catch (error) {
      setOtpVerifyFailed(true);
      setOtpErrorMsg(
        error.response?.data?.message ||
          "OTP verification failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendSignupOtp = async () => {
    setOtp(new Array(otpLength).fill(""));
    setOtpVerifyFailed(false);
    setOtpErrorMsg("");
    setOtpDeliveryStatus("");
    setOtpJobId("");
    setIsLoading(true);
    try {
      await requestSignupOtp(whatsappNumber);
      startResendTimer();
    } catch (error) {
      setOtpErrorMsg(
        error.response?.data?.message ||
          "OTP request failed, please try again later"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch pickup points function
  const fetchPickupPoints = async () => {
    try {
      setLoadingPickupPoints(true);
      const response = await axios.get(baseUrl + "/pickup-points");
      if (response.data.success) {
        setPickupPoints(response.data.data || []);
      } else {
        console.error("Failed to fetch pickup points:", response.data.message);
        setPickupPoints([]);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
      setPickupPoints([]);
    } finally {
      setLoadingPickupPoints(false);
    }
  };

  // Fetch pickup points when component mounts
  React.useEffect(() => {
    fetchPickupPoints();
  }, []);

  // Fetch provinces function
  const fetchProvinces = async () => {
    try {
      setLoadingProvinces(true);
      const response = await axios.get(baseUrl + "/provinces");
      setProvinces(response.data || []);
    } catch (error) {
      console.error("Error fetching provinces:", error);
      setProvinces([]);
    } finally {
      setLoadingProvinces(false);
    }
  };

  // Fetch provinces when component mounts
  React.useEffect(() => {
    fetchProvinces();
  }, []);

  // Fetch districts function
  const fetchDistricts = async (provinceId) => {
    if (!provinceId) {
      setDistricts([]);
      return;
    }

    try {
      setLoadingDistricts(true);
      const response = await axios.get(baseUrl + `/districts?pr_id=${provinceId}`);
      setDistricts(response.data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
      setDistricts([]);
    } finally {
      setLoadingDistricts(false);
    }
  };

  // Fetch districts when province is selected
  React.useEffect(() => {
    fetchDistricts(selectedProvince);
  }, [selectedProvince]);

  // WhatsApp number check handler
  const handleWhatsappCheck = async (event) => {
    event.preventDefault();

    const validation = validateWhatsappNumber(
      countryRules,
      countryCode,
      whatsappNumber
    );
    if (!validation.valid) {
      setErrorMsg(validation.message);
      return;
    }

    const fullWhatsappNumber = validation.fullNumber;
    setCheckingWhatsapp(true);
    setErrorMsg("");

    try {
      const response = await axios.post(baseUrl + "/check-whatsapp", {
        whatsapp: fullWhatsappNumber
      });

      if (response.data.exists) {
        setWhatsappExists(true);
        setErrorMsg("");
      } else {
        setWhatsappNumber(fullWhatsappNumber);
        // Send signup OTP, then move to verify step
        try {
          await requestSignupOtp(fullWhatsappNumber);
          startResendTimer();
          setStep("verify-otp");
        } catch (otpError) {
          setErrorMsg(
            otpError.response?.data?.message ||
              "Unable to send verification code. Please try again."
          );
        }
      }
    } catch (error) {
      console.error("Error checking WhatsApp:", error);
      setErrorMsg(
        error.response?.data?.message ||
          "Error checking WhatsApp number. Please try again."
      );
    } finally {
      setCheckingWhatsapp(false);
    }
  };

  // Signup form submission handler
  const handleSignupSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setIsLoading(true);
    setSignupFailed(false);
    setErrorMsg("");
    setFormErrors({});

    const signupData = {
      whatsapp: whatsappNumber,
      name: data.get("name"),
      password: data.get("password"),
      confirmPassword: data.get("confirmPassword"),
      isVientianeResident: isVientianeResident,
      // Vientiane resident fields
      defaultRoute: isVientianeResident ? defaultRoute : "",
      preferDoorToDoor: isVientianeResident ? preferDoorToDoor : false,
      deliveryAddresses: (isVientianeResident && preferDoorToDoor) ? deliveryAddresses : [],
      // Non-Vientiane resident fields
      selectedProvince: !isVientianeResident ? selectedProvince : "",
      selectedDistrict: !isVientianeResident ? selectedDistrict : "",
      selectedLogisticCompany: !isVientianeResident ? selectedLogisticCompany : "",
      branchName: !isVientianeResident ? branchName : "",
      addressDetail: !isVientianeResident ? addressDetail : ""
    };

    // Comprehensive validation
    const errors = validateForm(signupData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setErrorMsg("Please fix the errors below");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(baseUrl + "/customer/signup", signupData, {
        withCredentials: true
      });

      if (response.data.status === "ok") {
        if (response.data.customer) {
          const sanitizedCustomer = { ...response.data.customer };
          delete sanitizedCustomer.password;
          localStorage.setItem("customer", JSON.stringify(sanitizedCustomer));
          updateCustomerState(response.data.customer);
        }
        navigate("/customer-portal");
      }
    } catch (error) {
      console.error("Signup error:", error);
      if (error.response && error.response.data) {
        setErrorMsg(error.response.data.message || "Signup failed. Please try again.");
      } else {
        setErrorMsg("Network error. Please try again.");
      }
      setSignupFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  const resetWhatsappCheck = () => {
    setStep("whatsapp-check");
    setWhatsappExists(false);
    setWhatsappNumber("");
    setCountryCode("");
    setErrorMsg("");
    // Reset form fields
    setDefaultRoute("");
    setFormErrors({});
    // Reset OTP state
    setOtp(new Array(otpLength).fill(""));
    setOtpRef("");
    setOtpVerifyFailed(false);
    setOtpErrorMsg("");
    setResendSeconds(0);
    setResendDisabled(false);
  };

  // Validation functions
  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push("Password must contain at least one lowercase letter");
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push("Password must contain at least one uppercase letter");
    }
    if (!/(?=.*\d)/.test(password)) {
      errors.push("Password must contain at least one number");
    }
    return errors;
  };

  const validateForm = (formData) => {
    const errors = {};

    // Name validation
    if (!formData.name || formData.name.trim().length < 3) {
      errors.name = "Name must be at least 3 characters long";
    }

    // Password validation
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      errors.password = passwordErrors[0];
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    // Location validation
    if (isVientianeResident === null) {
      errors.vientianeResident = "Please specify if you live in Vientiane";
    }

    if (isVientianeResident === true) {
      // Vientiane residents must select pickup point
      if (!formData.defaultRoute || formData.defaultRoute.trim().length === 0) {
        errors.defaultRoute = "Please choose a pickup point";
      }

    } else if (isVientianeResident === false) {
      // Non-Vientiane residents must provide location details
      if (!selectedProvince) {
        errors.selectedProvince = "Please select your province";
      }
      if (!selectedDistrict) {
        errors.selectedDistrict = "Please select your district";
      }
      if (!selectedLogisticCompany) {
        errors.selectedLogisticCompany = "Please choose a logistic company";
      }
      if (!branchName || branchName.trim().length === 0) {
        errors.branchName = "Please provide the branch name";
      }
      if (!addressDetail || addressDetail.trim().length === 0) {
        errors.addressDetail = "Please provide your complete address";
      }
    }

    // Door-to-door delivery validation for Vientiane residents
    if (isVientianeResident && preferDoorToDoor && deliveryAddresses.length === 0) {
      errors.deliveryAddresses = "Please add at least one delivery address for door-to-door service";
    }

    return errors;
  };

  // Delivery address management functions (for door-to-door service)
  const addDeliveryAddress = () => {
    setDeliveryAddresses([...deliveryAddresses, {
      id: Date.now().toString(),
      map: "",
      title: "",
      description: "",
      isDefault: deliveryAddresses.length === 0
    }]);
  };

  const removeDeliveryAddress = (id) => {
    setDeliveryAddresses(deliveryAddresses.filter(addr => addr.id !== id));
  };

  const updateDeliveryAddress = (id, field, value) => {
    setDeliveryAddresses(deliveryAddresses.map(addr =>
      addr.id === id ? { ...addr, [field]: value } : addr
    ));
  };

  // Logistic companies for non-Vientiane residents
  const logisticCompanies = [
    "HAL Express",
    "Mixay Express",
    "Anousith Express"
  ];

  // Handle Vientiane residence selection
  const handleVientianeSelection = (event) => {
    const value = event.target.value;
    const isVientiane = value === "true";
    setIsVientianeResident(isVientiane);

    // Reset all location-related state
    setDefaultRoute("");
    setPreferDoorToDoor(false);
    setDeliveryAddresses([]);
    setSelectedProvince("");
    setSelectedDistrict("");
    setSelectedLogisticCompany("");
    setBranchName("");
    setAddressDetail("");

    // Clear form errors for location fields
    setFormErrors(prev => ({
      ...prev,
      vientianeResident: "",
      defaultRoute: "",
      selectedProvince: "",
      selectedDistrict: "",
      selectedLogisticCompany: "",
      branchName: "",
      addressDetail: ""
    }));

    // Fetch relevant data based on selection
    if (isVientiane) {
      fetchPickupPoints();
    } else {
      fetchProvinces();
    }
  };

  const inputSx = authInputSx(theme);
  const fieldLabelSx = authFieldLabelSx(theme);

  const features = [
    t("clientLogin.feature1"),
    t("clientLogin.feature2"),
    t("clientLogin.feature3"),
  ];

  const titles = {
    "whatsapp-check": ["Create your account", "We'll check if you already have an account"],
    "verify-otp": ["Verify your WhatsApp number", `We sent a 6-digit code to your WhatsApp at +${whatsappNumber}. Enter it below to continue.`],
    "signup-form": ["Complete your profile", `Complete your registration for: ${whatsappNumber}`],
  };
  const [title, subtitle] = titles[step];

  return (
    <AuthLayout tagline={t("clientLogin.tagline")} features={features} maxWidth={460}>
      <Box sx={{ mb: "34px" }}>
        <Typography sx={{ fontFamily: "'Space Grotesk', 'Noto Sans Lao', sans-serif", fontSize: 30, fontWeight: 700, color: "#1b1728", lineHeight: 1.2, mb: 1 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#9b8ab8", fontWeight: 500 }}>
          {subtitle}
        </Typography>
      </Box>

      {/* Step 1: WhatsApp Number Check */}
      {step === "whatsapp-check" && (
        <Box component="form" noValidate onSubmit={handleWhatsappCheck}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={fieldLabelSx}>Country Code</Typography>
            <FormControl fullWidth required>
              <Autocomplete
                id="country-code"
                name="countryCode"
                options={autocompleteOptions}
                disabled={checkingWhatsapp}
                getOptionLabel={(option) => option.label}
                value={
                  autocompleteOptions.find(
                    (option) => option.value === countryCode
                  ) || null
                }
                onChange={(event, newValue) => {
                  setCountryCode(newValue ? newValue.value : "");
                  if (whatsappExists) setWhatsappExists(false);
                  if (errorMsg) setErrorMsg("");
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select country code" sx={inputSx} />
                )}
              />
            </FormControl>
          </Box>

          <Box sx={{ mb: "14px" }}>
            <Typography component="label" htmlFor="whatsappNumber" sx={fieldLabelSx}>
              WhatsApp Number
            </Typography>
            <TextField
              required
              fullWidth
              id="whatsappNumber"
              name="whatsappNumber"
              placeholder="Enter your WhatsApp number"
              autoComplete="tel"
              autoFocus
              disabled={checkingWhatsapp}
              value={whatsappNumber}
              onChange={(e) => {
                setWhatsappNumber(e.target.value);
                if (whatsappExists) setWhatsappExists(false);
                if (errorMsg) setErrorMsg("");
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WhatsAppIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>

          {(whatsappExists || errorMsg) && (
            <Box sx={{ mb: 2 }}>
              {whatsappExists ? (
                <Alert
                  severity="info"
                  action={
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={() => navigate("/whatsapp-login")}
                    >
                      OK
                    </Button>
                  }
                >
                  We found your WhatsApp number in our system! Please login via WhatsApp to continue.
                </Alert>
              ) : (
                <Alert severity="error">{errorMsg}</Alert>
              )}
            </Box>
          )}

          <Button
            type="submit"
            disabled={checkingWhatsapp || whatsappExists}
            fullWidth
            variant="contained"
            color="secondary"
            sx={{ ...authButtonSx(theme), mb: 2 }}
          >
            {checkingWhatsapp ? "Checking..." : whatsappExists ? "Number Already Registered" : "Continue"}
          </Button>

          <FlexBetween>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => navigate("/login")}
              sx={{
                cursor: "pointer",
                fontWeight: 600,
                textDecoration: "none",
                color: theme.palette.secondary.main,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Already have an account? Sign in
            </Link>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => navigate("/whatsapp-login")}
              sx={{
                cursor: "pointer",
                fontWeight: 600,
                textDecoration: "none",
                color: theme.palette.secondary.main,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Login with WhatsApp
            </Link>
          </FlexBetween>
        </Box>
      )}

      {/* Step 2a: OTP Verification */}
      {step === "verify-otp" && (
        <Box>
          <OTPInput
            otp={otp}
            setOtp={setOtp}
            length={otpLength}
            onComplete={handleOtpComplete}
            fontSize="14px"
            boxWidth="18px"
          />

          {otpVerifyFailed && otpErrorMsg && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {otpErrorMsg}
            </Alert>
          )}

          {!otpVerifyFailed && otpDeliveryStatus && otpDeliveryStatus !== "sent" && (
            <Alert severity="info" sx={{ mt: 2 }}>
              We're sending OTP to {whatsappNumber.slice(0, 3)}{"X".repeat(Math.max(0, whatsappNumber.length - 6))}{whatsappNumber.slice(-3)}
            </Alert>
          )}

          <FlexBetween sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 13, color: "#9b8ab8" }}>
              Reference no. {otpRef}
            </Typography>
          </FlexBetween>

          <FlexBetween sx={{ mt: 2, gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={() => {
                setStep("whatsapp-check");
                setOtp(new Array(otpLength).fill(""));
                setOtpVerifyFailed(false);
                setOtpErrorMsg("");
                setOtpDeliveryStatus("");
                setOtpJobId("");
              }}
              disabled={isLoading}
              sx={{ borderRadius: "14px", height: 52, borderColor: "#e5dff5" }}
            >
              Change number
            </Button>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={handleResendSignupOtp}
              disabled={isLoading || resendDisabled}
              sx={authButtonSx(theme)}
            >
              {resendDisabled ? `Resend in ${resendSeconds}s` : "Resend OTP"}
            </Button>
          </FlexBetween>
        </Box>
      )}

      {/* Step 2: Signup Form */}
      {step === "signup-form" && (
        <Box component="form" onSubmit={handleSignupSubmit}>
          {signupFailed && errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography component="label" htmlFor="name" sx={fieldLabelSx}>
              Full Name
            </Typography>
            <TextField
              required
              fullWidth
              id="name"
              name="name"
              autoComplete="name"
              autoFocus
              placeholder="Enter your full name"
              error={!!formErrors.name}
              helperText={formErrors.name}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineOutlinedIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography component="label" htmlFor="password" sx={fieldLabelSx}>
              Password
            </Typography>
            <TextField
              required
              fullWidth
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              placeholder="Min 8 chars, uppercase, lowercase, number"
              error={!!formErrors.password}
              helperText={formErrors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography component="label" htmlFor="confirmPassword" sx={fieldLabelSx}>
              Confirm Password
            </Typography>
            <TextField
              required
              fullWidth
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              placeholder="Confirm your password"
              error={!!formErrors.confirmPassword}
              helperText={formErrors.confirmPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>

          {/* Vientiane Residency Question */}
          <FormControl
            component="fieldset"
            margin="normal"
            fullWidth
            error={!!formErrors.vientianeResident}
            sx={{ mt: 0 }}
          >
            <FormLabel component="legend" sx={{ ...fieldLabelSx, mb: 1 }}>
              Do you live in Vientiane Capital?
            </FormLabel>
            <RadioGroup
              value={isVientianeResident === null ? "" : isVientianeResident.toString()}
              onChange={handleVientianeSelection}
            >
              <FormControlLabel
                value="true"
                control={<Radio color="secondary" />}
                label="Yes, I live in Vientiane Capital"
              />
              <FormControlLabel
                value="false"
                control={<Radio color="secondary" />}
                label="No, I live outside Vientiane Capital"
              />
            </RadioGroup>
            {formErrors.vientianeResident && (
              <FormHelperText>{formErrors.vientianeResident}</FormHelperText>
            )}
          </FormControl>

          {/* Vientiane Residents - Pickup Points Selection */}
          {isVientianeResident === true && (
            <>
              <Box sx={{ mt: 2 }}>
                <Autocomplete
                  fullWidth
                  id="pickup-point-select"
                  options={pickupPoints}
                  getOptionLabel={(option) => option.name || ""}
                  value={pickupPoints.find(point => point.id === defaultRoute) || null}
                  onChange={(event, newValue) => {
                    setDefaultRoute(newValue ? newValue.id : "");
                    if (formErrors.defaultRoute) {
                      setFormErrors(prev => ({ ...prev, defaultRoute: "" }));
                    }
                  }}
                  loading={loadingPickupPoints}
                  disabled={loadingPickupPoints}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Choose Pickup Point"
                      required
                      placeholder={loadingPickupPoints ? "Loading pickup points..." : "Select your pickup location"}
                      error={!!formErrors.defaultRoute}
                      helperText={formErrors.defaultRoute || (loadingPickupPoints ? "Loading available pickup points..." : "Select where you want to pick up your packages")}
                      sx={inputSx}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {option.name}
                        </Typography>
                        {option.description && (
                          <Typography variant="body2" color="text.secondary">
                            {option.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                />
              </Box>

              {/* Door-to-door delivery option for Vientiane residents */}
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferDoorToDoor}
                      onChange={(e) => {
                        setPreferDoorToDoor(e.target.checked);
                        if (!e.target.checked) {
                          setDeliveryAddresses([]);
                        }
                      }}
                      color="secondary"
                    />
                  }
                  label="I prefer door-to-door delivery (additional charges apply)"
                />
              </Box>

              {preferDoorToDoor && (
                <Box sx={{ mt: 1, mb: 2, p: 2, border: 1, borderColor: '#e5dff5', borderRadius: "12px" }}>
                  <Typography sx={{ ...fieldLabelSx, mb: 2 }}>Delivery Addresses</Typography>
                  {formErrors.deliveryAddresses && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {formErrors.deliveryAddresses}
                    </Alert>
                  )}
                  {deliveryAddresses.map((address) => (
                    <Card key={address.id} sx={{ mb: 2, p: 2, borderRadius: "12px" }} variant="outlined">
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Address Title"
                              value={address.title}
                              onChange={(e) => updateDeliveryAddress(address.id, 'title', e.target.value)}
                              required
                              sx={inputSx}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Map Location/GPS"
                              value={address.map}
                              onChange={(e) => updateDeliveryAddress(address.id, 'map', e.target.value)}
                              sx={inputSx}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Full Address Description"
                              value={address.description}
                              onChange={(e) => updateDeliveryAddress(address.id, 'description', e.target.value)}
                              multiline
                              rows={3}
                              required
                              sx={inputSx}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={address.isDefault}
                                    onChange={(e) => updateDeliveryAddress(address.id, 'isDefault', e.target.checked)}
                                    color="secondary"
                                  />
                                }
                                label="Set as default"
                              />
                              {deliveryAddresses.length > 1 && (
                                <IconButton
                                  onClick={() => removeDeliveryAddress(address.id)}
                                  color="error"
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addDeliveryAddress}
                    variant="outlined"
                    color="secondary"
                    sx={{ mt: 1, borderRadius: "14px" }}
                  >
                    Add Delivery Address
                  </Button>
                </Box>
              )}
            </>
          )}

          {/* Non-Vientiane Residents - Provincial Location & Logistic Company */}
          {isVientianeResident === false && (
            <>
              <Typography sx={{ ...fieldLabelSx, mt: 2, mb: 2 }}>
                Provincial Location & Service Details
              </Typography>

              {/* Province Selection */}
              <Box sx={{ mb: 2 }}>
                <Autocomplete
                  fullWidth
                  id="province-select"
                  options={provinces}
                  getOptionLabel={(option) => option.pr_name_en || option.pr_name || ""}
                  value={provinces.find(province => province.pr_id === selectedProvince) || null}
                  onChange={(event, newValue) => {
                    setSelectedProvince(newValue ? newValue.pr_id : "");
                    setSelectedDistrict(""); // Reset district when province changes
                    setDistricts([]); // Clear districts
                    if (newValue) {
                      fetchDistricts(newValue.pr_id);
                    }
                    if (formErrors.selectedProvince) {
                      setFormErrors(prev => ({ ...prev, selectedProvince: "" }));
                    }
                  }}
                  loading={loadingProvinces}
                  disabled={loadingProvinces}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Province"
                      required
                      placeholder={loadingProvinces ? "Loading provinces..." : "Choose your province"}
                      error={!!formErrors.selectedProvince}
                      helperText={formErrors.selectedProvince || (loadingProvinces ? "Loading available provinces..." : "Select your province")}
                      sx={inputSx}
                    />
                  )}
                />
              </Box>

              {/* District Selection */}
              <Box sx={{ mb: 2 }}>
                <Autocomplete
                  fullWidth
                  id="district-select"
                  options={districts}
                  getOptionLabel={(option) => option.dt_name_en || option.dt_name || ""}
                  value={districts.find(district => district.dt_id === selectedDistrict) || null}
                  onChange={(event, newValue) => {
                    setSelectedDistrict(newValue ? newValue.dt_id : "");
                    if (formErrors.selectedDistrict) {
                      setFormErrors(prev => ({ ...prev, selectedDistrict: "" }));
                    }
                  }}
                  loading={loadingDistricts}
                  disabled={loadingDistricts || !selectedProvince}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select District"
                      required
                      placeholder={!selectedProvince ? "Please select province first" : loadingDistricts ? "Loading districts..." : "Choose your district"}
                      error={!!formErrors.selectedDistrict}
                      helperText={formErrors.selectedDistrict || (!selectedProvince ? "Select province first" : loadingDistricts ? "Loading available districts..." : "Select your district")}
                      sx={inputSx}
                    />
                  )}
                />
              </Box>

              {/* Logistic Company Selection */}
              <FormControl fullWidth required error={!!formErrors.selectedLogisticCompany} sx={{ mb: 2 }}>
                <InputLabel>Logistic Company</InputLabel>
                <Select
                  value={selectedLogisticCompany}
                  onChange={(e) => {
                    setSelectedLogisticCompany(e.target.value);
                    if (formErrors.selectedLogisticCompany) {
                      setFormErrors(prev => ({ ...prev, selectedLogisticCompany: "" }));
                    }
                  }}
                  label="Logistic Company"
                  sx={inputSx}
                >
                  {logisticCompanies.map((company) => (
                    <MenuItem key={company} value={company}>
                      {company}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.selectedLogisticCompany && (
                  <FormHelperText>{formErrors.selectedLogisticCompany}</FormHelperText>
                )}
              </FormControl>

              {/* Branch Name */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={fieldLabelSx}>Branch Name</Typography>
                <TextField
                  required
                  fullWidth
                  value={branchName}
                  onChange={(e) => {
                    setBranchName(e.target.value);
                    if (formErrors.branchName) {
                      setFormErrors(prev => ({ ...prev, branchName: "" }));
                    }
                  }}
                  placeholder="Enter the branch name where you'll collect packages"
                  error={!!formErrors.branchName}
                  helperText={formErrors.branchName}
                  sx={inputSx}
                />
              </Box>

              {/* Address Detail */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={fieldLabelSx}>Complete Address</Typography>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={3}
                  value={addressDetail}
                  onChange={(e) => {
                    setAddressDetail(e.target.value);
                    if (formErrors.addressDetail) {
                      setFormErrors(prev => ({ ...prev, addressDetail: "" }));
                    }
                  }}
                  placeholder="Enter your complete address (village, street, landmarks, etc.)"
                  error={!!formErrors.addressDetail}
                  helperText={formErrors.addressDetail}
                  sx={inputSx}
                />
              </Box>
            </>
          )}

          <FlexBetween sx={{ mt: 2, mb: 2, gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={resetWhatsappCheck}
              disabled={isLoading}
              sx={{ borderRadius: "14px", height: 52, borderColor: "#e5dff5" }}
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              fullWidth
              disabled={isLoading}
              sx={authButtonSx(theme)}
            >
              {isLoading ? "Creating Account..." : "Create Account"}
            </Button>
          </FlexBetween>

          <Box sx={{ textAlign: "center" }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => navigate("/login")}
              sx={{
                cursor: "pointer",
                fontWeight: 600,
                textDecoration: "none",
                color: theme.palette.secondary.main,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Already have an account? Sign in
            </Link>
          </Box>
        </Box>
      )}
    </AuthLayout>
  );
}
